import { CircularProgress, Step, StepLabel, Stepper } from "@mui/material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box } from "@mui/system";
import { CorporationsEntity } from "entities/Corporations/CorporationsEntity";
import useSteps from "hooks/useSteps";
import { useTranslation } from "react-i18next";
import { useStyles } from "./Corporations.styles";
import GeneralInformationStep from "./GeneralInformationStep";
import MillSettingsStep from "./MillSettingsStep/MillSettingsStep.view";
import { CORPORATE_BACKOFFICE_PATHS } from "constants/constants";
import useRolesPermissions from "hooks/useRolesPermissions";
import { useContext, useEffect } from "react";
import NoPermissions from "pages/Corporate/components/NoPermissions/NoPermissions.view";
import NoData from "components/NoData/NoData.view";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";

enum StepsEnum {
    corporationReview,
    millSettings,
}

export const Corporations = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const millId = searchParams.get("millId");
    const { CORPORATE_PERMISSIONS } = useRolesPermissions();
    const {
        loading: isLoadingData,
        mill,
        corporation,
        refetch,
    } = CorporationsEntity.GetCorporationAndMillBackOffice(Number(millId));
    const { refreshCorporationsAndDivisions } = useContext(CorporateContext);
    const steps = [
        {
            id: StepsEnum.corporationReview,
            label: t("general_information.title"),
        },
        {
            id: StepsEnum.millSettings,
            label: t("mill_setup.title"),
        },
    ];

    const { activeStep, handleNextStep, handlePreviousStep, setStepManually } = useSteps({
        steps,
        initialStep: StepsEnum.corporationReview,
    });

    useEffect(() => {
        if (CORPORATE_PERMISSIONS?.corporationPermissions?.onlyMillSettings || Boolean(millId)) {
            setStepManually(StepsEnum.millSettings);
        }
    }, [CORPORATE_PERMISSIONS?.corporationPermissions?.onlyMillSettings, setStepManually, millId]);

    const generalInformationOnFinish = async () => {
        await refetch();
        if (!CORPORATE_PERMISSIONS?.corporationPermissions?.onlyGeneralInformation) {
            handleNextStep();
        } else {
            refreshCorporationsAndDivisions();
            navigate(`/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/0`);
        }
        window.scrollTo(0, 0);
    };

    const millSettingsOnFinish = async () => {
        await refetch();

        navigate(
            `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${mill.mill_id}`,
        );

        window.scrollTo(0, 0);
    };

    const millSettingsOnBackStep = async () => {
        await refetch();
        handlePreviousStep();
        window.scrollTo(0, 0);
    };

    if (isLoadingData)
        return (
            <div style={{ marginTop: 32 }}>
                <div>{t("general_information.loading_corporate_and_mill_data")}</div>
                <Box sx={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                    <CircularProgress color="info" />
                </Box>
            </div>
        );

    if (!mill)
        return (
            <div style={{ marginTop: 32 }}>
                <div>{t("general_information.loading_corporate_and_mill_data")}</div>
                <Box sx={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
                    <NoData />
                </Box>
            </div>
        );

    const stepsComponents = {
        [StepsEnum.corporationReview]: {
            component: (
                <GeneralInformationStep
                    corporation={corporation}
                    onFinish={generalInformationOnFinish}
                    onlyGeneralInformationStep={CORPORATE_PERMISSIONS?.corporationPermissions?.onlyGeneralInformation}
                />
            ),
        },
        [StepsEnum.millSettings]: {
            component: (
                <MillSettingsStep
                    mill={mill}
                    onFinish={millSettingsOnFinish}
                    onBackStep={millSettingsOnBackStep}
                    onlyMillSettings={CORPORATE_PERMISSIONS?.corporationPermissions?.onlyMillSettings}
                />
            ),
        },
    };
    return CORPORATE_PERMISSIONS?.corporationPermissions?.canEditSettings ? (
        <div className={classes.contentWrapper}>
            {CORPORATE_PERMISSIONS.corporationPermissions.showStepper && (
                <Stepper activeStep={activeStep.id}>
                    {steps.map((step) => (
                        <Step key={step.id} className={classes.step}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}

            {
                // @ts-ignore
                stepsComponents[activeStep.id].component
            }
        </div>
    ) : (
        <NoPermissions />
    );
};

export default Corporations;
